import { PermissionWithAssignedT, PermissionI } from 'types';

export type PermissionWithAssigned = PermissionI & { assigned: boolean };

export interface PermissionGroup {
  prefix: string;
  name: string;
  permissions: PermissionWithAssigned[];
}

const MIN_GROUP_SIZE = 2;

const prefixOf = (key: string): string => key.split('.')[0] ?? key;

const resolveGroupName = (prefix: string, t2: (k: string) => string): string => {
  const translated = t2(`groups.${prefix}`);
  // useTranslation returns the key if missing; fall back to a humanized prefix.
  if (translated && translated !== `groups.${prefix}`) return translated;
  return prefix.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
};

/**
 * Splits permissions into:
 *  - singles: permissions whose key prefix appears only once.
 *  - groups: permissions whose key prefix appears 2+ times (e.g. session.read, session.create...).
 *
 * Preserves the original order: each group takes the position of its first member.
 */
export function groupPermissions(
  permissions: PermissionWithAssignedT,
  t2: (k: string) => string,
): Array<{ kind: 'single'; permission: PermissionWithAssigned } | { kind: 'group'; group: PermissionGroup }> {
  const counts = new Map<string, number>();
  permissions.forEach(p => {
    const prefix = prefixOf(p.key);
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1);
  });

  const seen = new Set<string>();
  const result: Array<
    { kind: 'single'; permission: PermissionWithAssigned } | { kind: 'group'; group: PermissionGroup }
  > = [];

  permissions.forEach(p => {
    const prefix = prefixOf(p.key);
    const total = counts.get(prefix) ?? 0;

    if (total >= MIN_GROUP_SIZE) {
      if (seen.has(prefix)) return;
      seen.add(prefix);
      const members = permissions.filter(x => prefixOf(x.key) === prefix);
      result.push({
        kind: 'group',
        group: {
          prefix,
          name: resolveGroupName(prefix, t2),
          permissions: members,
        },
      });
    } else {
      result.push({ kind: 'single', permission: p });
    }
  });

  return result;
}
